// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Snackbar from "@mui/material/Snackbar";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Fade from "@mui/material/Fade";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Custom styles for the MDSnackbar
import MDSnackbarIconRoot from "components/MDSnackbar/MDSnackbarIconRoot";
import { useSelector } from "react-redux";

function MDSnackbar({ color, icon, content, close, bgWhite, ...rest }) {
  const controller = useSelector((state) => state.materialUI);
  const { darkMode } = controller;

  let titleColor;
  let dateTimeColor;
  let dividerColor;

  if (bgWhite) {
    titleColor = color;
    dateTimeColor = "dark";
    dividerColor = false;
  } else if (color === "light") {
    titleColor = darkMode ? "inherit" : "dark";
    dateTimeColor = darkMode ? "inherit" : "text";
    dividerColor = false;
  } else {
    titleColor = "white";
    dateTimeColor = "white";
    dividerColor = true;
  }

  return (
    <Snackbar
      TransitionComponent={Fade}
      autoHideDuration={5000}
      anchorOrigin={rest.anchorOrigin || {
        vertical: "top",
        horizontal: "right",
      }}
      {...rest}
      action={
        <IconButton size="small" aria-label="close" color="inherit" onClick={close}>
          <Icon fontSize="small">close</Icon>
        </IconButton>
      }
    >
      <MDBox
        variant={bgWhite ? "contained" : "gradient"}
        bgColor={bgWhite ? "white" : color}
        minWidth="21.875rem"
        shadow="md"
        borderRadius="md"
        p={1}
        sx={{
          maxWidth: "min(100%, calc(100vw - 32px))",
          backgroundColor: ({ palette }) =>
            darkMode ? palette.background.card : palette[color] || palette.white.main,
        }}
      >
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          color="dark"
          p={1.5}
        >
          <MDBox
            display="flex"
            alignItems="center"
            sx={{ flex: 1, minWidth: 0, pr: 1 }}
          >
            <MDSnackbarIconRoot
              fontSize="small"
              ownerState={{ color, bgWhite }}
              sx={{ flexShrink: 0 }}
            >
              {icon}
            </MDSnackbarIconRoot>
            <MDBox
              p={1}
              sx={{
                flex: 1,
                minWidth: 0,
                whiteSpace: "normal",
                overflowWrap: "break-word",
                wordBreak: "break-word",
                lineHeight: 1.4,
                fontSize: ({ typography: { size } }) => size.sm,
                fontWeight: ({ typography: { fontWeightMedium } }) => fontWeightMedium,
                color: ({ palette: { white, text } }) => {
                  let colorValue = bgWhite || color === "light" ? text.main : white.main;

                  if (darkMode) {
                    colorValue = color === "light" ? "inherit" : white.main;
                  }

                  return colorValue;
                },
              }}
            >
              {content}
            </MDBox>
          </MDBox>
          <MDBox display="flex" alignItems="center" lineHeight={0} sx={{ flexShrink: 0 }}>
            <Icon
              sx={{
                color: ({ palette: { dark, white } }) =>
                  (bgWhite && !darkMode) || color === "light" ? dark.main : white.main,
                fontWeight: ({ typography: { fontWeightBold } }) => fontWeightBold,
                cursor: "pointer",
                marginLeft: 2,
              }}
              onClick={close}
            >
              close
            </Icon>
          </MDBox>
        </MDBox>
      </MDBox>
    </Snackbar>
  );
}

// Setting default values for the props of MDSnackbar
MDSnackbar.defaultProps = {
  bgWhite: false,
  color: "info",
};

// Typechecking props for MDSnackbar
MDSnackbar.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
  ]),
  icon: PropTypes.node.isRequired,
  content: PropTypes.node.isRequired,
  close: PropTypes.func.isRequired,
  bgWhite: PropTypes.bool,
};

export default MDSnackbar;